/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';

import {
  Checkbox,
  Grid,
  Paper,
  Table,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
} from '@mui/material';
import { DateTime } from 'luxon';

import {
  BasePrintComponentProps,
  IDeliveryNote,
  IDeliveryNoteInvoiceInfo,
} from 'commons/types';
import { formatCurrency } from 'commons/utils/formatCurrency';

import S from './DNPrintTemplate.styles';

const FULL_PAGE_ROW_COUNT = 17;
const RETURN_TABLE_ROW_COUNT = 12;
// Header + invoice table + return table + signature section only fit on one
// physical A4 page up to ~7 invoice rows; beyond that the content overflows
// onto a near-blank second page, so this stays conservative rather than
// packing the page to its exact limit.
const FOOTER_PAGE_ROW_COUNT = 7;

type FooterLayout = 'inline' | 'split' | 'separate';

interface InvoicePrintPage {
  invoices: IDeliveryNoteInvoiceInfo[];
  startIndex: number;
}

const paginateInvoices = (
  invoices: IDeliveryNoteInvoiceInfo[],
): { pages: InvoicePrintPage[]; footerLayout: FooterLayout } => {
  if (!invoices.length) {
    return {
      pages: [{ invoices: [], startIndex: 0 }],
      footerLayout: 'inline',
    };
  }

  if (invoices.length <= FOOTER_PAGE_ROW_COUNT) {
    return { pages: [{ invoices, startIndex: 0 }], footerLayout: 'inline' };
  }

  const pages: InvoicePrintPage[] = [];
  let remaining = invoices;
  let startIndex = 0;
  while (remaining.length > FULL_PAGE_ROW_COUNT) {
    pages.push({
      invoices: remaining.slice(0, FULL_PAGE_ROW_COUNT),
      startIndex,
    });
    startIndex += FULL_PAGE_ROW_COUNT;
    remaining = remaining.slice(FULL_PAGE_ROW_COUNT);
  }
  pages.push({ invoices: remaining, startIndex });

  let footerLayout: FooterLayout = 'inline';
  if (remaining.length > RETURN_TABLE_ROW_COUNT) {
    footerLayout = 'separate';
  } else if (remaining.length > FOOTER_PAGE_ROW_COUNT) {
    footerLayout = 'split';
  }

  return { pages, footerLayout };
};

interface DNPrintTemplateComponentProps
  extends BasePrintComponentProps<IDeliveryNote> {
  copyNumber?: number; // to adjust footer position if this component is copy version of template printed
  offsetStart?: number; // to adjust start of footer position if dn is merged with other template
  setDnPageBreakFromPageNumber?: (pageNumber: number) => void;
}

export const DNPrintTemplateComponent = ({
  data,
  copyNumber = 0,
  offsetStart = 0,
  setDnPageBreakFromPageNumber,
}: DNPrintTemplateComponentProps): React.JSX.Element => {
  const { pages: invoicePages, footerLayout } = paginateInvoices(
    data.invoices || [],
  );
  const [totalPages, setTotalPages] = useState<number>(0);
  const [pageOffsetBase, setPageOffsetBase] = useState<number>(0);
  // determine max page data
  useEffect(() => {
    const maxPageNumber =
      invoicePages.length + (footerLayout === 'inline' ? 0 : 1);
    setTotalPages(maxPageNumber);
    setPageOffsetBase(maxPageNumber * copyNumber + offsetStart);
    if (setDnPageBreakFromPageNumber)
      setDnPageBreakFromPageNumber(maxPageNumber);
  }, [
    invoicePages,
    footerLayout,
    copyNumber,
    offsetStart,
    setDnPageBreakFromPageNumber,
  ]);

  const renderReturnTable = (): React.JSX.Element => (
    <TableContainer component={Paper} style={{ boxShadow: 'none' }}>
      <br />
      <Table
        sx={{
          minWidth: 650,
          [`& .${tableCellClasses.root}`]: {
            borderBottom: 'none',
            fontFamily: 'Arial',
            fontSize: 10,
          },
        }}
        size="small"
        aria-label="return table"
      >
        <S.ReturnTableTitle>
          <TableRow>
            <TableCell colSpan={9}>
              BERITA ACARA RETURN SAAT PENGIRIMAN (WAJIB DIISI JIKA ADA PRODUK
              RETURN DAN ISI DATA DI BARCODE RETURN)
            </TableCell>
          </TableRow>
        </S.ReturnTableTitle>
        <S.TableHeadStyle>
          <TableRow>
            <S.TableHeaderCell style={{ width: '4%' }}>No</S.TableHeaderCell>
            <S.TableHeaderCell style={{ width: '16%' }}>
              No INV
            </S.TableHeaderCell>
            <S.TableHeaderCell style={{ width: '17%' }}>
              Nama Produk
            </S.TableHeaderCell>
            <S.TableHeaderCell style={{ width: '12%' }}>
              No SKU
            </S.TableHeaderCell>
            <S.TableHeaderCell style={{ width: '17%' }}>UoM</S.TableHeaderCell>
            <S.TableHeaderCell style={{ width: '12%' }}>
              Batch
            </S.TableHeaderCell>
            <S.TableHeaderCell style={{ width: '9%' }}>
              Expiry Date
            </S.TableHeaderCell>
            <S.TableHeaderCell style={{ width: '5%' }}>Qty</S.TableHeaderCell>
            <S.TableHeaderCell style={{ width: '8%' }}>
              Kode Return
            </S.TableHeaderCell>
          </TableRow>
        </S.TableHeadStyle>
        <S.TableBodyStyle>
          {Array.from({ length: 7 }).map((_, i) => (
            <S.TableRowStyle key={i} sx={{ height: '6mm !important' }}>
              <S.TableBodyCell />
              <S.TableBodyCell />
              <S.TableBodyCell />
              <S.TableBodyCell />
              <S.TableBodyCell />
              <S.TableBodyCell />
              <S.TableBodyCell />
              <S.TableBodyCell />
              <S.TableBodyCell />
            </S.TableRowStyle>
          ))}
        </S.TableBodyStyle>
      </Table>
    </TableContainer>
  );

  const renderSignatureSection = (): React.JSX.Element => (
    <S.DNFooterWrapper container>
      <S.SignatureSectionWrapper item xs={5}>
        <Grid container>
          <Grid item xs={4}>
            <p>
              <b>Diserahkan oleh</b>
            </p>
            <S.SignatureHeader>Pihak Apotek</S.SignatureHeader>
            <p>Nama Apotek</p>
          </Grid>
          <Grid item xs={4}>
            <p>
              <b>Diterima oleh</b>
            </p>
            <S.SignatureHeader>Pihak Logistik</S.SignatureHeader>
            <p>Nama Driver</p>
          </Grid>
          <Grid item xs={4}>
            <p>
              <b>Diterima oleh</b>
            </p>
            <S.SignatureHeader>Pihak TMP</S.SignatureHeader>
            <p>Nama PIC TMP</p>
          </Grid>
        </Grid>
      </S.SignatureSectionWrapper>
      <Grid item xs={7}>
        <S.ReturnCodeTable>
          <thead>
            <tr>
              <S.ReturnCodeTh colSpan={2}>Kode Alasan Return</S.ReturnCodeTh>
            </tr>
          </thead>
          <tbody>
            <tr>
              <S.ReturnCodeItemCell style={{ width: '37.5%' }}>
                <b>A:</b> Produk rusak / basah saat pengiriman
              </S.ReturnCodeItemCell>
              <S.ReturnCodeItemCell style={{ width: '37.5%' }}>
                <b>G:</b> Jumlah Produk tidak sesuai dengan Faktur (salah
                picking)
              </S.ReturnCodeItemCell>
            </tr>
            <tr>
              <S.ReturnCodeItemCell>
                <b>B:</b> Relasi salah pesan / salah input pesanan
              </S.ReturnCodeItemCell>
              <S.ReturnCodeItemCell>
                <b>H:</b> Item Produk tidak sesuai dengan faktur (salah picking,
                salah batch)
              </S.ReturnCodeItemCell>
            </tr>
            <tr>
              <S.ReturnCodeItemCell>
                <b>C:</b> Salah alamat pengiriman
              </S.ReturnCodeItemCell>
              <S.ReturnCodeItemCell>
                <b>I:</b> Produk mendekati kadaluarsa
              </S.ReturnCodeItemCell>
            </tr>
            <tr>
              <S.ReturnCodeItemCell>
                <b>D:</b> Pesanan double
              </S.ReturnCodeItemCell>
              <S.ReturnCodeItemCell>
                <b>J:</b> Tidak ada informasi (Expiry, No Bets, NIE)
              </S.ReturnCodeItemCell>
            </tr>
            <tr>
              <S.ReturnCodeItemCell>
                <b>E:</b> Selisih harga / diskon
              </S.ReturnCodeItemCell>
              <S.ReturnCodeItemCell>
                <b>K:</b> Waktu pengiriman tidak sesuai (late delivery)
              </S.ReturnCodeItemCell>
            </tr>
            <tr>
              <S.ReturnCodeItemCell>
                <b>F:</b> SP relasi expired
              </S.ReturnCodeItemCell>
              <S.ReturnCodeItemCell>
                <b>L:</b> Toko pindah lokasi
              </S.ReturnCodeItemCell>
            </tr>
          </tbody>
        </S.ReturnCodeTable>
      </Grid>
    </S.DNFooterWrapper>
  );

  const renderPageFooter = (
    pageNumber: number,
    pageOffset: number,
  ): React.JSX.Element => (
    <S.PageFooter $pageOffset={pageOffset}>
      <QRCode size={88} value={data.qrcode_value} />
      <S.PageNumberText>
        Page {pageNumber} of {totalPages}
      </S.PageNumberText>
    </S.PageFooter>
  );

  const renderHeader = (): React.JSX.Element => (
    <>
      <S.PageHeader>
        <S.PageHeaderContent container>
          <S.HeaderSectionWrapper container item xs={5}>
            <S.DeliveryNoteTitle item xs={12}>
              Delivery Note
            </S.DeliveryNoteTitle>
            <S.CompanyName item xs={12}>
              {data.company_name}
            </S.CompanyName>
            <S.CompanyAddress item xs={12}>
              {data.company_address}
            </S.CompanyAddress>
          </S.HeaderSectionWrapper>
          <S.HeaderSectionWrapper container item xs={7}>
            <Grid item xs={3} />
            <Grid item container xs={9}>
              <S.AWBNumber item xs={6}>
                No AWB:
              </S.AWBNumber>
              <S.AWBNumber item xs={6}>
                {data.awb_number}
              </S.AWBNumber>
              <S.OrderDate item xs={6}>
                Tanggal:
              </S.OrderDate>
              <S.OrderDate item xs={6}>
                {DateTime.fromISO(
                  data.ordered_at as unknown as string,
                ).toFormat('dd-LLL-yyyy')}
              </S.OrderDate>
              <S.DueDate item xs={6}>
                Tanggal Jatuh Tempo:
              </S.DueDate>
              <S.DueDate item xs={6}>
                {DateTime.fromISO(data.due_date as unknown as string).toFormat(
                  'dd-LLL-yyyy',
                )}
              </S.DueDate>
              <Grid item xs={6} />
              <S.DeliveryType item xs={6}>
                <Checkbox
                  checked={data.is_regular_delivery}
                  sx={{
                    p: 0,
                    '&.Mui-checked': { color: 'gray' },
                    transform: 'scale(0.75)',
                    transformOrigin: 'left center',
                  }}
                />{' '}
                Regular Delivery
              </S.DeliveryType>
              <Grid item xs={6} />
              <S.DeliveryType item xs={6}>
                <Checkbox
                  checked={data.is_coldchain_delivery}
                  sx={{
                    p: 0,
                    '&.Mui-checked': { color: 'gray' },
                    transform: 'scale(0.75)',
                    transformOrigin: 'left center',
                  }}
                />{' '}
                Cold Chain (2 - 8 °C)
              </S.DeliveryType>
            </Grid>
          </S.HeaderSectionWrapper>
        </S.PageHeaderContent>
      </S.PageHeader>
      <Grid container sx={{ gap: '4px', flexWrap: 'nowrap' }}>
        <S.CustomerInfo container sx={{ flex: 1 }}>
          <Grid item xs={4}>
            Cust ID:
          </Grid>
          <Grid item xs={8}>
            {data.pharmacy_id}
          </Grid>
          <Grid item xs={4}>
            Nama Customer:
          </Grid>
          <Grid item xs={8}>
            {data.pharmacy_name}
          </Grid>
        </S.CustomerInfo>
        <S.CustomerInfo container sx={{ flex: 1 }}>
          <Grid item xs={3}>
            Alamat Apotek:
          </Grid>
          <Grid item xs={9}>
            {data.pharmacy_address}
          </Grid>
        </S.CustomerInfo>
      </Grid>
    </>
  );

  const renderBody = (
    invoices: IDeliveryNoteInvoiceInfo[],
    startIndex: number,
  ): React.JSX.Element => (
    <S.PageBody>
      <TableContainer component={Paper} style={{ boxShadow: 'none' }}>
        <Table
          sx={{
            minWidth: 650,
            [`& .${tableCellClasses.root}`]: {
              borderBottom: 'none',
              fontFamily: 'Arial',
              fontSize: 10,
            },
          }}
          size="small"
          aria-label="invoice table"
        >
          <S.TableHeadStyle>
            <TableRow>
              <S.TableHeaderCell>No</S.TableHeaderCell>
              <S.TableHeaderCell>No Invoice</S.TableHeaderCell>
              <S.TableHeaderCell>Total Koli</S.TableHeaderCell>
              <S.TableHeaderCell style={{ width: '8%' }}>
                Tipe Dokumen*
              </S.TableHeaderCell>
              <S.TableHeaderCell>Amount</S.TableHeaderCell>
              <S.TableHeaderCell style={{ width: '15%' }}>
                Kondisi Penerimaan**
              </S.TableHeaderCell>
              <S.TableHeaderCell style={{ width: '17%' }}>
                Retur, jika ada**
              </S.TableHeaderCell>
              <S.TableHeaderCell style={{ width: '17%' }}>
                Nama, TTD Penerima, dan Stempel
              </S.TableHeaderCell>
            </TableRow>
          </S.TableHeadStyle>
          <S.TableBodyStyle>
            {invoices.map((invoice, idx) => (
              <S.TableRowStyle
                key={`InvTableRow-${invoice.invoice_number}-${idx}`}
              >
                <S.TableBodyCell>{startIndex + idx + 1}</S.TableBodyCell>
                <S.TableBodyCell>
                  {invoice.invoice_number?.replace(' ', '')}
                </S.TableBodyCell>
                <S.TableBodyCell>{`${invoice.koli_count} Box`}</S.TableBodyCell>
                <S.TableBodyCell>
                  {invoice.is_digital_signed ? 'Elektronik' : 'Manual'}
                </S.TableBodyCell>
                <S.TableBodyCell>
                  {formatCurrency(invoice.invoiced_amount || 0)}
                </S.TableBodyCell>
                <S.TableBodyCell sx={{ textAlign: 'left !important' }}>
                  <S.CheckboxLabel>
                    <Checkbox
                      checked={false}
                      size="small"
                      sx={{
                        p: 0,
                        '&.Mui-checked': { color: 'gray' },
                        transform: 'scale(0.75)',
                        transformOrigin: 'left center',
                      }}
                    />{' '}
                    Lengkap
                  </S.CheckboxLabel>
                  <S.CheckboxLabel>
                    <Checkbox
                      checked={false}
                      size="small"
                      sx={{
                        p: 0,
                        '&.Mui-checked': { color: 'gray' },
                        transform: 'scale(0.75)',
                        transformOrigin: 'left center',
                      }}
                    />{' '}
                    Tidak Lengkap
                  </S.CheckboxLabel>
                </S.TableBodyCell>
                <S.TableBodyCell sx={{ textAlign: 'left !important' }}>
                  <S.CheckboxLabel>
                    <Checkbox
                      checked={false}
                      size="small"
                      sx={{
                        p: 0,
                        '&.Mui-checked': { color: 'gray' },
                        transform: 'scale(0.75)',
                        transformOrigin: 'left center',
                      }}
                    />{' '}
                    Full Faktur
                  </S.CheckboxLabel>
                  <S.CheckboxLabel>
                    <Checkbox
                      checked={false}
                      size="small"
                      sx={{
                        p: 0,
                        '&.Mui-checked': { color: 'gray' },
                        transform: 'scale(0.75)',
                        transformOrigin: 'left center',
                      }}
                    />{' '}
                    Retur Sebagian
                  </S.CheckboxLabel>
                </S.TableBodyCell>
                {idx === 0 && (
                  <S.TableBodyCell
                    rowSpan={invoices.length}
                    style={{ verticalAlign: 'top' }}
                  />
                )}
              </S.TableRowStyle>
            ))}
          </S.TableBodyStyle>
        </Table>
      </TableContainer>
      <S.InvoiceTableInfo container>
        <Grid item xs={12}>
          *Tipe dokumen manual wajib menyertakan Surat Pesanan Asli
        </Grid>
        <Grid item xs={12}>
          **Ceklis yang sesuai
        </Grid>
      </S.InvoiceTableInfo>
    </S.PageBody>
  );

  return (
    <div>
      <S.DNPrintGlobalStyle />
      <S.PageWrapper>
        {invoicePages.map(({ invoices: chunk, startIndex }, pageIdx) => {
          const isLastInvoicePage = pageIdx === invoicePages.length - 1;
          const needsPageBreak =
            !isLastInvoicePage || footerLayout !== 'inline';

          return (
            <React.Fragment key={`dn-page-${pageIdx}`}>
              {renderHeader()}
              {renderBody(chunk, startIndex)}
              {isLastInvoicePage &&
                footerLayout !== 'separate' &&
                renderReturnTable()}
              {isLastInvoicePage &&
                footerLayout === 'inline' &&
                renderSignatureSection()}
              {renderPageFooter(pageIdx + 1, pageOffsetBase + pageIdx)}
              {needsPageBreak && <div className="pagebreak-after" />}
            </React.Fragment>
          );
        })}
        {footerLayout !== 'inline' && (
          <>
            {renderHeader()}
            {footerLayout === 'separate' && renderReturnTable()}
            {renderSignatureSection()}
            {renderPageFooter(totalPages, pageOffsetBase + invoicePages.length)}
          </>
        )}
      </S.PageWrapper>
    </div>
  );
};
